/* eslint-disable @typescript-eslint/no-unused-vars */
import { Drawer, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "../../../components";
import { FormProvider, FormState } from "../../../components/FormProvider";
import { Family } from "../../../models";
import { useDeleteFamilyMutation, useSaveFamilyMutation } from "../../../redux/apiStore";
import { EditFamilyForm } from "./EditFamilyForm";

export function EditFamilyDrawer(props: { family?: Family; open: boolean; onClose: () => void }) {
  const { family, open, onClose } = props;
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const formDataGetterRef = useRef<(() => Family | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const dialogs = useDialogs();
  const [saveFamily, saveFamilyStatus] = useSaveFamilyMutation();
  const [deleteFamily, deleteFamilyStatus] = useDeleteFamilyMutation();

  const goToFamilyDetail = (family: Family) => {
    navigate(`/families/${family.id}`);
  };

  const handleSave = async (form: FormState) => {
    const family = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!family) {
      form.notifyError("Failed to save family. Family is null for some reason");
      return;
    }
    try {
      const result = await saveFamily(family).unwrap();
      onClose();
      form.notifySuccess("Family saved");
      goToFamilyDetail(result);
    } catch (err) {
      form.notifyError("Error saving family", err);
    }
  };

  const handleCancel = async (form: FormState) => {
    try {
      if (resetFormRef.current) {
        resetFormRef.current();
        onClose();
      } else {
        console.error("Failed to undo form changes. No form reset method has been set.");
      }
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  const handleDelete = async (form: FormState) => {
    const family = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!family) {
      form.notifyError("Failed to delete family. Family is null for some reason");
      return;
    }
    try {
      const confirmed = await dialogs.confirm(`Are you sure you want to delete ${family.name}?`);
      if (confirmed) {
        const result = await deleteFamily({ id: family.id }).unwrap();
        form.notifySuccess("Family deleted");
        navigate("/families");
      } else {
        console.log("Family not deleted");
      }
    } catch (err) {
      form.notifyError("Error deleting family", err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : "400px", // 100% width on mobile
            zIndex: 1550,
          },
        },
      }}
    >
      <Stack sx={{ p: 2, height: "100%" }}>
        <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={handleDelete} showControls={false}>
          <EditFamilyForm family={family} dataGetterRef={formDataGetterRef} resetFormRef={resetFormRef} />
        </FormProvider>
      </Stack>
    </Drawer>
  );
}

export default EditFamilyDrawer;
